import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

const management = 'management/';
const client = 'clients';

const apiRoutes = {
	dashboard: management + client + '/dashboard',
	clients: management + client + '/list',
	createClient: management + client + '/create',
	updateClientStatus: management + client + '/status',
	updateClientBucketCustomDomain: management + client + '/bucket-custom-domain',
	updateClientTemplates: management + client + '/templates',
	client_users: management + client + '/users',
	client_plans: management + client + '/plans',
	client_plan: management + client + '/plan',
	client_logs: management + client + '/logs',
	client_api_key: management + client + '/api-key',
}

@Injectable({
  providedIn: 'root'
})
export class ClientsService {

	private clientsList: any[] = [];
	templatesList: any[] = [];
	private _clients$ = new BehaviorSubject<any[]>([]);

	private _client$ = new Subject<any>();
  private _currentClient!: any;

	private menuButtonClickSubject = new Subject<void>();
  menuButtonClick$ = this.menuButtonClickSubject.asObservable();

	private leftSideBarButton = new Subject<any>();
  leftSideBarEvent$ = this.leftSideBarButton.asObservable();

	loading = true;

	constructor(private http: HttpClient) { }

	getClientDashboard() {
		return this.http.get<any>(environment.api + apiRoutes.dashboard);
	}

	getClientsTree() {
		return this.http.get<any>(environment.api + apiRoutes.clients);
	}

	getClientUsers(client_id: number, params: any) {
		return this.http.get<any>(environment.api + apiRoutes.client_users + '/' + client_id, { params: params });
	}

	getClientPlans(client_id: number) {
		return this.http.get<any>(environment.api + apiRoutes.client_plans + '/' + client_id);
	}

	getClientLogs(client_id: number) {
		return this.http.get<any>(environment.api + apiRoutes.client_logs + '/' + client_id);
	}

	updateClientStatus(client_id: number, data: any) {
		return this.http.patch<any>(environment.api + apiRoutes.updateClientStatus + '/' + client_id, data);
	}

	updateClientBucketCustomDomain(client_id: number, data: any) {
		return this.http.patch<any>(environment.api + apiRoutes.updateClientBucketCustomDomain + '/' + client_id, data);
	}

	updateClientTemplates(client_id: number, data: any) {
		return this.http.patch<any>(environment.api + apiRoutes.updateClientTemplates + '/' + client_id, data);
	}

	createClientPlan(client_id: number, data: any) {
		return this.http.post<any>(environment.api + apiRoutes.client_plan + '/' + client_id, data);
	}

	updateClientPlan(client_id: number, plan_id: number, data: any) {
		return this.http.patch<any>(environment.api + apiRoutes.client_plan + '/' + client_id + '/' + plan_id, data);
	}

	cancelClientPlan(client_id: number, plan_id: number) {
		return this.http.patch<any>(environment.api + apiRoutes.client_plan + '/' + client_id + '/' + plan_id + '/cancel', {});
	}

	createClient(data: any) {
		return this.http.post<any>(environment.api + apiRoutes.createClient, data);
	}

	getApiKey() {
		return this.http.get<any>(environment.api + apiRoutes.client_api_key);
	}

	createApiKey() {
		return this.http.post<any>(environment.api + apiRoutes.client_api_key, {});
	}

	reGenerateApiKey() {
		return this.http.patch<any>(environment.api + apiRoutes.client_api_key, {});
	}

	loadPostmanCollection(filePath: string) {
    return this.http.get<any>(filePath); 
  }

	async getClients(): Promise<void> {  
    try {
			const data = await firstValueFrom(this.getClientsTree());
      this.clientsList = data.clients;
			if (this.templatesList.length === 0 || this.templatesList !== data.templates) {
				this.templatesList = data.templates;
			}

      this._clients$.next(data.clients);
      this.loading = false;
    } catch (error) {
      this.clientsList = [];
      this._clients$.next([]);
      this.loading = false;
    }
  }

	get clients$() { return this._clients$.asObservable(); }
	get client$() { return this._client$.asObservable() }

	get client() {
		return this._currentClient;
	}

	setClient(client: any) {
    this._currentClient = client;
    this._client$.next(client);
		this.leftSideBarButton.next(false);
  }

  getClientsList() {
    return this.clientsList;
  }

  getClient() {
    return this._currentClient;
  }

	setLeftSideBar(shouldHide: boolean) {
    this.leftSideBarButton.next(shouldHide);
  }

	triggerMenuButtonClick() {
    this.menuButtonClickSubject.next();
  }
}
